"use client";
import { CssBaseline, VeBox, useMediaQuery } from "@packages/ui/mui";
import * as React from "react";

import "./sideBar.scss";

import {} from "@public/index";
import { usePathname, useRouter } from "next/navigation";

import { useSession } from "next-auth/react";
import StickyFooter from "@components/footer/userFooter";
import { useData } from "@providers/context";
import AppBarUser from "./appBar";
import TabDrawerUser from "./tabDrawerUser";
import WebDrawerUser from "./webDrawerUser";
import { useQuery, useQueryClient } from "@tanstack/react-query";

interface MenuItemData {
  text: string;
  path?: string;

  icon?: JSX.Element;
  selectedIcon?: JSX.Element;
  subItems?: MenuItemData[];
}

const drawerWidth = 250;

interface Props {
  children: React.ReactNode;
  dynamicRoute?: string;
}

export default function ResponsiveDrawer(props: Props) {
  const { children } = props;
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);

  const pathName = usePathname();
  const isProjectPage = pathName.startsWith("/project");

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  const [anchorElUserTab, setAnchorElUserTab] =
    React.useState<null | HTMLElement>(null);

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
    setOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenUserMenuTab = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUserTab(event.currentTarget);
  };

  const handleCloseUserMenuTab = () => {
    setAnchorElUserTab(null);
  };

  const [isHovered, setIsHovered] = React.useState(false);
  const [open, setOpen] = React.useState(!isProjectPage);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerCloseWeb = () => {
    setOpen(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen((prevState) => !prevState);
    }
  };

  const [selectedItem, setSelectedItem] = React.useState("Dashbord");
  const [childSelected, setChildSelected] = React.useState("");

  React.useEffect(() => {
    const storedSelectedItem = localStorage.getItem("selectedSideBar");
    const storedChildSelected = localStorage.getItem("selectedSubItem");

    // Check if storedSelectedItem is not null before setting state
    if (storedSelectedItem !== null) {
      setSelectedItem(storedSelectedItem);
    }

    // Check if storedChildSelected is not null before setting state
    if (storedChildSelected !== null) {
      setChildSelected(storedChildSelected);
    }
  }, []);

  const handleItemClick = (text: string) => {
    if (selectedItem !== text) {
      setSelectedItem(text);
      setChildSelected("");
      // localStorage.setItem("selectedSideBar", text);
    } else {
      setSelectedItem("");
      // localStorage.setItem("selectedSideBar", text);
    }
  };

  const isTab = useMediaQuery("(max-width: 900px)");
  const queryClient = useQueryClient();
  const data: any = queryClient.getQueryData(["userProfile"]);

  const prevWidthRef = React.useRef(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const prevWidth = prevWidthRef.current;

      // Example: 768px breakpoint (mobile/tablet → desktop)
      if (prevWidth <= 768 && currentWidth > 768) {
        setOpen(true); // switched to desktop view
      }

      // Update previous width
      prevWidthRef.current = currentWidth;
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const pathname = usePathname();

  React.useEffect(() => {
    // Clean up body overflow on route change
    return () => {
      document.body.style.overflow = "";
    };
  }, [pathname]);

  const { currentRole } = useData();
  const sellerDashBoardCondition =
    currentRole === "SELLER" && pathName === "/dashboard";

  return (
    // Project Developer / Reviewer code
    <VeBox sx={{ position: "relative" }}>
      <AppBarUser
        data={data}
        open={open}
        handleDrawerToggle={handleDrawerToggle}
        anchorElUserTab={anchorElUserTab}
        handleCloseUserMenuTab={handleCloseUserMenuTab}
        handleOpenUserMenuTab={handleOpenUserMenuTab}
      />
      <VeBox
        sx={{
          display: "flex",
          position: "relative",
          height: "calc(100vh - 48px)",
        }}
        // className="max-width-lg"
      >
        <CssBaseline />
        {!sellerDashBoardCondition && (
          <VeBox
            component="nav"
            sx={{
              flexShrink: { sm: 0 },
              // zIndex: "499 !important",
            }}
            aria-label="mailbox folders"
          >
            <TabDrawerUser
              mobileOpen={mobileOpen}
              handleDrawerClose={handleDrawerClose}
              handleDrawerTransitionEnd={handleDrawerTransitionEnd}
              open={open}
              selectedItem={selectedItem}
              childSelected={childSelected}
              handleItemClick={handleItemClick}
              setChildSelected={setChildSelected}
              pathName={pathName}
              setIsHovered={setIsHovered}
              anchorElUser={anchorElUser}
              handleCloseUserMenu={handleCloseUserMenu}
              handleOpenUserMenu={handleOpenUserMenu}
              data={data}
            />
            <WebDrawerUser
              handleDrawerClose={handleDrawerClose}
              handleDrawerTransitionEnd={handleDrawerTransitionEnd}
              open={open}
              selectedItem={selectedItem}
              childSelected={childSelected}
              handleItemClick={handleItemClick}
              setChildSelected={setChildSelected}
              pathName={pathName}
              setIsHovered={setIsHovered}
              anchorElUser={anchorElUser}
              handleCloseUserMenu={handleCloseUserMenu}
              handleDrawerCloseWeb={handleDrawerCloseWeb}
              handleDrawerOpen={handleDrawerOpen}
              isHovered={isHovered}
            />
          </VeBox>
        )}
        <VeBox
          sx={{ overflow: "scroll" }}
          width={
            sellerDashBoardCondition
              ? "100vw"
              : isTab
                ? "calc(100vw - 0px)"
                : open
                  ? `calc(100vw - ${drawerWidth}px)`
                  : `calc(100vw - 52px)`
          }
          className="ads"
          id="scroll-window"
        >
          <VeBox
            component="main"
            sx={{
              // paddingTop: !isTab ? "16px" : "4rem",
              minHeight: "90vh",
              background: "white",
            }}
          >
            {children}
          </VeBox>

          {!sellerDashBoardCondition && (
            <VeBox
              sx={{
                width: {
                  md: "100%",
                },
              }}
            >
              <StickyFooter />
            </VeBox>
          )}
        </VeBox>
      </VeBox>
    </VeBox>
  );
}
